import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import type { Secret } from '../../interfaces/secrets.interface';

/**
 * Reusable secret picker: a `<mat-select>` of the project's secrets bound to a
 * caller-supplied {@link FormControl}, with the "no secrets available → go to
 * Settings" fallback panel shown when the list is empty.
 *
 * Extracted from the `#secretSelectField` template in `add-connection-dialog`
 * (database / LLM / object-storage / service all reused it). The component is
 * presentational: it owns no data loading and emits {@link goToSettings} so the
 * host decides how to navigate to the secrets settings page.
 */
@Component({
  selector: 'app-secret-picker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './secret-picker.component.html',
  styleUrl: './secret-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecretPickerComponent {
  /** The form control holding the selected secret id. */
  readonly control = input.required<FormControl>();
  /** The secrets available for selection. */
  readonly secrets = input.required<Secret[]>();
  /** Field label. */
  readonly label = input('Secret');
  /** Validation error message to display, or `null` when valid. */
  readonly error = input<string | null>(null);
  /** Extra class applied to the `<mat-form-field>` (defaults to full width). */
  readonly fieldClass = input('full-width');

  /** Emitted when the user clicks the "go to Settings" action in the empty state. */
  readonly goToSettings = output<void>();
}
